import { supabase } from '@/lib/supabaseClient';

// Weekly-grading trend detection. A crew is "downtrending" when its
// performance_metrics total_points are falling week-on-week:
//   - two or more consecutive weekly drops, OR
//   - a single-week drop of DROP_ALERT_THRESHOLD points or more, OR
//   - its final grading fell a band this week (e.g. Good -> Average).
export const TREND_WEEKS = 8;
const DROP_ALERT_THRESHOLD = 5;
// Gradings are entered per-crew on different days, so a crew that declined last
// week may not have a row on the single newest global grading date. Treat a crew
// as "still active" (and therefore eligible) if its latest grading falls within
// the most recent RECENCY_DATES distinct grading dates — not only the newest one.
const RECENCY_DATES = 4;

const GRADE_RANK = { champion: 4, good: 3, average: 2, unacceptable: 1 };

export const gradeRank = (grade) => GRADE_RANK[String(grade || '').trim().toLowerCase()] ?? null;

export const fetchTrendMetrics = async (weeks = TREND_WEEKS) => {
    const { data: dates, error: datesError } = await supabase
        .from('performance_metrics')
        .select('grading_date')
        .order('grading_date', { ascending: false })
        .limit(2000);

    if (datesError) throw datesError;

    const recentDates = [...new Set((dates || []).map((d) => d.grading_date))].slice(0, weeks);
    if (recentDates.length === 0) return { rows: [], dates: [] };

    const { data: rows, error: rowsError } = await supabase
        .from('performance_metrics')
        .select(`
            id, crew_id, grading_date, week_number, total_points, final_grading,
            volume, no_shows_1w, no_show_points, weekend_availability_4w,
            damage_4w, stp_4w, pre_calls_1w, photos_1w, samsara_1w,
            crews:crew_id ( id, crew_identifier, depots (name) )
        `)
        // Trends stay on the DEPOT (core) score: since the week-24 electric
        // split, premier electric crews carry a second 'electric' row per
        // week, which would double-count their weeks here.
        .eq('grading_variant', 'core')
        .in('grading_date', recentDates);

    if (rowsError) throw rowsError;

    return { rows: rows || [], dates: recentDates.sort() };
};

export const computeDowntrends = (rows, dates) => {
    if (!rows?.length || !dates?.length) return [];

    const recentActive = new Set(dates.slice(-RECENCY_DATES));
    const byCrew = new Map();

    rows.forEach((row) => {
        if (!row.crew_id) return;
        if (!byCrew.has(row.crew_id)) byCrew.set(row.crew_id, []);
        byCrew.get(row.crew_id).push(row);
    });

    const results = [];

    byCrew.forEach((crewRows, crewId) => {
        const series = [...crewRows]
            .sort((a, b) => String(a.grading_date).localeCompare(String(b.grading_date)))
            // Duplicate protection mirrors AdminPerformanceHub's dedupe by date.
            .filter((row, i, arr) => i === 0 || row.grading_date !== arr[i - 1].grading_date);

        if (series.length < 2) return;

        const latest = series[series.length - 1];
        if (!recentActive.has(latest.grading_date)) return; // not graded recently — inactive

        const prev = series[series.length - 2];
        const points = series.map((r) => Number(r.total_points ?? 0));
        const latestDelta = points[points.length - 1] - points[points.length - 2];

        let declineStreak = 0;
        for (let i = points.length - 1; i > 0; i--) {
            if (points[i] < points[i - 1]) declineStreak += 1;
            else break;
        }

        const latestRank = gradeRank(latest.final_grading);
        const prevRank = gradeRank(prev.final_grading);
        const gradeDropped = latestRank != null && prevRank != null && latestRank < prevRank;

        const isDowntrending =
            latestDelta < 0 &&
            (declineStreak >= 2 || latestDelta <= -DROP_ALERT_THRESHOLD || gradeDropped);

        if (!isDowntrending) return;

        results.push({
            crew_id: crewId,
            crew_identifier: latest.crews?.crew_identifier || 'Unknown',
            depot_name: latest.crews?.depots?.name || 'Unknown',
            series,
            latest,
            prev,
            latestPoints: points[points.length - 1],
            prevPoints: points[points.length - 2],
            latestDelta,
            declineStreak,
            gradeDropped,
            periodStart: series[0].grading_date,
            periodEnd: latest.grading_date,
        });
    });

    return results.sort((a, b) => a.latestDelta - b.latestDelta);
};

export const buildReportPayload = (trend) => ({
    generated_at: new Date().toISOString(),
    crew_identifier: trend.crew_identifier,
    depot_name: trend.depot_name,
    period: { start: trend.periodStart, end: trend.periodEnd },
    stats: {
        latest_points: trend.latestPoints,
        prev_points: trend.prevPoints,
        latest_delta: trend.latestDelta,
        decline_streak: trend.declineStreak,
        latest_grade: trend.latest.final_grading,
        prev_grade: trend.prev.final_grading,
        grade_dropped: trend.gradeDropped,
    },
    // Strip the joined crews object — the viewer only needs the metric rows.
    series: trend.series.map(({ crews, ...row }) => row),
});

export const trendSummaryText = (trend) =>
    `${trend.crew_identifier} dropped ${Math.abs(trend.latestDelta)} pts this week` +
    (trend.declineStreak >= 2 ? ` (${trend.declineStreak} weeks declining)` : '') +
    (trend.gradeDropped ? `, grade fell to ${trend.latest.final_grading}` : '') +
    '.';
